'use client';

import { useMemo, useState } from 'react';
import { CheckIcon, CopyIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type CodeBlockVariant = 'light' | 'dark';

type CodeBlockProps = {
  code: string;
  language: string;
  variant?: CodeBlockVariant;
  copied?: boolean;
  onCopy?: () => void;
  defaultWrap?: boolean;
  showWrapToggle?: boolean;
  collapsible?: boolean;
  maxHeight?: string;
  className?: string;
};

const CODE_FONT_FAMILY =
  '"JetBrains Mono", "Fira Code", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';
const LARGE_CODE_CHAR_LIMIT = 200_000;
const PREVIEW_CHAR_LIMIT = 20_000;

export function CodeBlock({
  code,
  language,
  variant = 'light',
  copied = false,
  onCopy,
  defaultWrap,
  showWrapToggle,
  collapsible,
  maxHeight,
  className,
}: CodeBlockProps) {
  const [wrap, setWrap] = useState(Boolean(defaultWrap));
  const [expanded, setExpanded] = useState(false);

  const isLarge = code.length > LARGE_CODE_CHAR_LIMIT;
  const lineCount = useMemo(() => {
    if (isLarge) return 0;
    return code.split('\n').length;
  }, [code, isLarge]);
  const canCollapse = Boolean(collapsible) && (isLarge || lineCount > 12);
  const isExpanded = expanded || !canCollapse;
  const displayedCode = useMemo(() => {
    if (isExpanded) return code;
    if (code.length <= PREVIEW_CHAR_LIMIT) return code;
    return `${code.slice(0, PREVIEW_CHAR_LIMIT)}\n…(обрезано)`;
  }, [code, isExpanded]);

  const hasToolbar = Boolean(showWrapToggle || onCopy);
  const isDark = variant === 'dark';
  const controlClassName = isDark
    ? 'rounded px-2 py-1 text-xs text-[var(--site-primary-text)] hover:bg-[var(--site-primary-fill)]'
    : 'rounded px-2 py-1 text-xs text-[var(--site-ink)] hover:bg-[color:rgba(23,38,59,0.12)]';
  const copyButtonClassName = isDark
    ? 'rounded p-1 hover:bg-[var(--site-primary-fill)]'
    : 'rounded p-1 hover:bg-[color:rgba(23,38,59,0.12)]';

  return (
    <div className={cn('relative', className)}>
      <div
        className={cn(
          'overflow-hidden rounded-lg border',
          isDark ? 'border-transparent bg-[#0e111a]' : 'bg-muted',
        )}
      >
        {hasToolbar ? (
          <div className="flex items-center justify-end gap-2 px-3 pt-3">
            {showWrapToggle ? (
              <button type="button" onClick={() => setWrap((value) => !value)} className={controlClassName}>
                {wrap ? 'Без переноса' : 'Перенос'}
              </button>
            ) : null}
            {onCopy ? (
              <button type="button" onClick={onCopy} className={copyButtonClassName} aria-label="Скопировать код">
                {copied ? (
                  <CheckIcon className={cn('h-5 w-5', isDark ? 'text-green-400' : 'text-green-600')} />
                ) : (
                  <CopyIcon className={cn('h-5 w-5', isDark ? 'text-[var(--site-ink-subtle)]' : 'text-[var(--site-ink)]')} />
                )}
              </button>
            ) : null}
          </div>
        ) : null}

        <pre
          className={cn(
            'overflow-auto bg-transparent p-4 text-xs leading-5',
            hasToolbar && 'pt-2',
            isDark ? 'text-[var(--site-primary-text)]' : 'text-[var(--site-ink-strong)]',
            canCollapse && !isExpanded && !maxHeight && 'max-h-[280px] sm:max-h-[360px] lg:max-h-[420px]',
          )}
          style={{
            margin: 0,
            fontSize: 12,
            maxHeight: maxHeight ?? undefined,
            whiteSpace: wrap ? 'pre-wrap' : 'pre',
            wordBreak: wrap ? 'break-word' : 'normal',
          }}
        >
          <code
            style={{
              fontFamily: CODE_FONT_FAMILY,
              background: 'transparent',
            }}
            data-language={language}
          >
            {displayedCode}
          </code>
        </pre>
      </div>

      {canCollapse ? (
        <div className="mt-2 flex justify-end">
          <Button variant="outline" size="sm" onClick={() => setExpanded((value) => !value)}>
            {isExpanded ? 'Свернуть' : 'Развернуть'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
